import qs from 'qs';

import type {
  GetAttractionResponse,
  ListAttractionCitiesResponse,
  ListAttractionImagesResponse,
  ListAttractionOffersParams,
  ListAttractionOffersResponse,
  ListAttractionsParams,
  ListAttractionsResponse,
} from '~/types/attractionTypes';

import { json_headers, request } from './common';

type RequestOptions = {
  signal?: AbortSignal;
};

const BASE_PATH = `${window.configs.API_HOST}/api/experiences/attractions`;

/**
 * Lists attractions (admin endpoint, includes inactive).
 */
export async function listAttractions(
  params: ListAttractionsParams,
  options: RequestOptions = {},
): Promise<ListAttractionsResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/admin?${query}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Fetches a single attraction by ID.
 */
export async function getAttraction(
  attractionId: string,
  options: RequestOptions = {},
): Promise<GetAttractionResponse> {
  return request(`${BASE_PATH}/${attractionId}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Fetches cities with attraction coverage data.
 */
export async function getAttractionCities(options: RequestOptions = {}): Promise<ListAttractionCitiesResponse> {
  return request(`${BASE_PATH}/cities`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Fetches offers matched to an attraction.
 */
export async function getAttractionOffers(
  attractionId: string,
  params: ListAttractionOffersParams,
  options: RequestOptions = {},
): Promise<ListAttractionOffersResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  return request(`${BASE_PATH}/${attractionId}/offers?${query}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Toggles attraction active/inactive visibility.
 */
export async function updateAttractionActive(
  attractionId: string,
  active: boolean,
  options: RequestOptions = {},
): Promise<void> {
  return request(`${BASE_PATH}/${attractionId}/active`, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify({ active }),
    headers: json_headers,
  });
}

/**
 * Fetches available images for an attraction from linked offers.
 */
export async function getAttractionImages(
  attractionId: string,
  options: RequestOptions = {},
): Promise<ListAttractionImagesResponse> {
  return request(`${BASE_PATH}/${attractionId}/images`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Sets the hero image for an attraction.
 */
export async function updateAttractionImage(
  attractionId: string,
  imageId: string,
  options: RequestOptions = {},
): Promise<void> {
  return request(`${BASE_PATH}/${attractionId}/image`, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify({ imageId }),
    headers: json_headers,
  });
}

/**
 * Resets the attraction hero image to auto-selected.
 */
export async function resetAttractionImage(attractionId: string, options: RequestOptions = {}): Promise<void> {
  return request(`${BASE_PATH}/${attractionId}/image`, {
    ...options,
    method: 'DELETE',
  });
}
